import { AMOUNT_QUOTE_DEBOUNCE_MS } from "../../utils/amountQuote";
import { useEffect, useMemo, useRef, useState } from "react";
import { ethers } from "ethers";
import type { StoredJoinSplitNote } from "../../joinsplit/types";
import { flatRelayFee } from "../../joinsplit-v2/relayFee";
import {
  POOL_RELAY_QUOTE_REFRESH_MARGIN_MS,
  estimateWithdrawalShape,
  fetchPoolRelayQuote,
  poolRelayQuoteKey,
  quotedRelayFee,
  usesPoolRelayQuotes,
  type PoolRelayQuote,
  type PoolRelayQuoteShape,
} from "../../joinsplit-v2/poolRelayQuote";

/** The public withdrawal the send form describes, if any. */
export interface RelayFeeQuoteInput {
  kind: "withdraw" | "withdraw_batch";
  token: string;
  /** Single: the withdrawn amount. Batch: the recipient total (the fee is
   * charged on top of it). */
  amount: bigint;
  /** Preserve row edits even if a batch's total and priced shape stay equal. */
  amounts?: string[];
  recipients: string[];
}

export interface RelayFeeQuoteState {
  /** The relayer fee for an amount, or null while no fresh quote covers the
   * form on a quoted network (Base keeps its flat fee function). */
  relayFee: ((amount: bigint) => bigint) | null;
  quote: PoolRelayQuote | null;
  /** Shown instead of the fee while a quote is loading or unavailable. */
  pendingMessage: string | null;
}

const DEBOUNCE_MS = AMOUNT_QUOTE_DEBOUNCE_MS;
const RETRY_MS = 10_000;

/** Live relayer fee for the send form on a network priced by pre-proof
 * quotes (DogeOS Chikyu): the same note selection the settlement will make,
 * a debounced quote, and a refresh before each quote expires. */
export function useRelayFeeQuote(params: {
  input: RelayFeeQuoteInput | null;
  notes: StoredJoinSplitNote[];
  account?: string | null;
  paused?: boolean;
}): RelayFeeQuoteState {
  const quoted = usesPoolRelayQuotes();
  const [state, setState] = useState<{
    key: string | null;
    scope?: string;
    quote: PoolRelayQuote | null;
    /** On this device's clock: when the request started plus the quote's
     * lifetime. The quote was issued after that instant, so it is never
     * held past its expiry, whatever the relayer's clock says. */
    expiresAt: number;
    error: string | null;
  }>({ key: null, quote: null, expiresAt: 0, error: null });
  const [now, setNow] = useState(() => Date.now());
  const scope = JSON.stringify([
    params.account,
    params.input?.kind,
    params.input?.token,
    params.input?.recipients,
    params.input?.amount.toString(),
    params.input?.amounts,
  ]);
  const shape: PoolRelayQuoteShape | null = useMemo(() => {
    const input = params.input;
    if (!quoted || !input || input.amount <= 0n) return null;
    try {
      const token = ethers.getAddress(input.token);
      const recipients = input.recipients.map((r) => ethers.getAddress(r));
      // A batch spends its total plus the fee, so its notes are selected with
      // the fee the held quote in this token implies (the share before one).
      const held =
        state.quote && ethers.getAddress(state.quote.request.token) === token
          ? state.quote.fee
          : "0";
      const spent =
        input.kind === "withdraw_batch"
          ? input.amount + quotedRelayFee(input.amount, { fee: held })
          : input.amount;
      const estimate = estimateWithdrawalShape(params.notes, token, spent);
      return estimate && { kind: input.kind, token, ...estimate, recipients };
    } catch {
      return null;
    }
  }, [quoted, params.input, params.notes, state.quote]);
  const key = shape ? poolRelayQuoteKey(shape) : null;
  // Amount edits restart estimation even when the priced shape is unchanged.
  // Balance refreshes that preserve the form and shape do not restart it.
  const shapeRef = useRef(shape);
  shapeRef.current = shape;

  useEffect(() => {
    if (params.paused) return;
    const shape = shapeRef.current;
    if (!shape || !key) return;
    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    const load = async () => {
      const requestedAt = Date.now();
      try {
        const quote = await fetchPoolRelayQuote(shape, {
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        const expiresAt = requestedAt + quote.expiresAt - quote.issuedAt;
        setState({ key, scope, quote, expiresAt, error: null });
        setNow(Date.now());
        timer = setTimeout(
          load,
          Math.max(
            1_000,
            expiresAt - Date.now() - POOL_RELAY_QUOTE_REFRESH_MARGIN_MS,
          ),
        );
      } catch (error) {
        if (controller.signal.aborted) return;
        setState((previous) => ({
          key,
          scope,
          // Keep a still-valid quote for this form through a failed refresh.
          quote:
            previous.key === key && previous.scope === scope
              ? previous.quote
              : null,
          expiresAt:
            previous.key === key && previous.scope === scope
              ? previous.expiresAt
              : 0,
          error:
            error instanceof Error && error.message
              ? error.message
              : "The network fee is unavailable right now.",
        }));
        setNow(Date.now());
        timer = setTimeout(load, RETRY_MS);
      }
    };
    timer = setTimeout(load, DEBOUNCE_MS);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [key, scope, params.paused]);

  // Re-render when the held quote expires, so it is never used stale.
  useEffect(() => {
    if (params.paused) return;
    const expiresAt = state.quote ? state.expiresAt : 0;
    if (!expiresAt || expiresAt <= now) return;
    const timer = setTimeout(() => setNow(Date.now()), expiresAt - now + 1);
    return () => clearTimeout(timer);
  }, [state.quote, state.expiresAt, now, params.paused]);

  const fresh =
    state.scope === scope &&
    state.quote &&
    (params.paused ||
      (key && state.key === key && state.expiresAt > Date.now()))
      ? state.quote
      : null;
  // Stable per quote, so callers memoizing on it re-render only on a new one.
  const quotedFee = useMemo(
    () => (fresh ? (amount: bigint) => quotedRelayFee(amount, fresh) : null),
    [fresh],
  );
  if (!quoted)
    return { relayFee: flatRelayFee, quote: null, pendingMessage: null };
  if (fresh && quotedFee)
    return { relayFee: quotedFee, quote: fresh, pendingMessage: null };
  if (!key) return { relayFee: null, quote: null, pendingMessage: null };
  return {
    relayFee: null,
    quote: null,
    pendingMessage:
      state.key === key && state.error
        ? `Network fee unavailable: ${state.error}`
        : "Fetching the current network fee…",
  };
}
